import React, { useCallback, useEffect, useRef, useState } from "react";

type UsePdfPaginationReturn = {
  numPages: number;
  setNumPages: React.Dispatch<React.SetStateAction<number>>;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  pageInputValue: string;
  setPageInputValue: React.Dispatch<React.SetStateAction<string>>;
  renderedPages: number;
  setRenderedPages: React.Dispatch<React.SetStateAction<number>>;
  pageRefs: React.MutableRefObject<(HTMLDivElement | null)[]>;
  handlePreviousPage: () => void;
  handleNextPage: () => void;
  handlePageInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleUpdateCurrentPage: () => void;
};

export function usePdfPagination(
  containerRef: React.RefObject<HTMLDivElement | null>,
  zoomLevel: number,
  initialPage?: number,
  // When OCR highlight rects exist, PdfPreview owns the initial scroll (to the
  // first rect, not the page top), so suppress this one to avoid a competing
  // scroll (MDRS-134).
  suppressInitialScroll = false
): UsePdfPaginationReturn {
  const [numPages, setNumPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInputValue, setPageInputValue] = useState("1");
  const [renderedPages, setRenderedPages] = useState(0);
  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);

  // When a caller wants the preview to open on a specific page (e.g. a
  // semantic-search citation, MDRS-122), scroll to it once every page has
  // rendered. The page *number* is selected by PdfPreview's onLoadSuccess;
  // this effect owns only the *scroll*, deferred past the onRenderSuccess
  // auto-update (which measures the top-visible page and would otherwise snap
  // us back to page 1) so the cited page wins. `hasScrolledToInitial` ensures
  // it fires only on load, never fighting the user's later navigation.
  const hasScrolledToInitial = useRef(false);
  useEffect(() => {
    hasScrolledToInitial.current = false;
  }, [initialPage]);
  useEffect(() => {
    if (suppressInitialScroll) return;
    if (initialPage == null || hasScrolledToInitial.current) return;
    if (numPages <= 0 || renderedPages < numPages) return;

    hasScrolledToInitial.current = true;
    const target = Math.min(Math.max(initialPage, 1), numPages);
    const timer = setTimeout(() => {
      pageRefs.current[target - 1]?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 150);
    return () => clearTimeout(timer);
  }, [initialPage, numPages, renderedPages, suppressInitialScroll]);

  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isDraggingScrollbar = useRef(false);

  // Reset page refs when numPages changes
  useEffect(() => {
    pageRefs.current = Array(numPages).fill(null);
  }, [numPages]);

  const handleUpdateCurrentPage = useCallback(() => {
    if (!containerRef.current) return;

    const scrollElement = containerRef.current.querySelector(
      "[data-radix-scroll-area-viewport]"
    );
    if (!scrollElement) return;

    let visiblePage = 1;
    let maxVisibleArea = 0;

    pageRefs.current.forEach((ref, index) => {
      if (!ref) return;

      const rect = ref.getBoundingClientRect();
      const scrollRect = scrollElement.getBoundingClientRect();

      const visibleTop = Math.max(rect.top, scrollRect.top);
      const visibleBottom = Math.min(rect.bottom, scrollRect.bottom);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);
      const visibleArea = visibleHeight * rect.width;

      if (visibleArea > maxVisibleArea) {
        maxVisibleArea = visibleArea;
        visiblePage = index + 1;
      }
    });

    if (visiblePage !== currentPage) {
      setCurrentPage(visiblePage);
      setPageInputValue(visiblePage.toString());
    }
  }, [currentPage, containerRef]);

  const debouncedUpdatePage = useCallback(() => {
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    const timeout = isDraggingScrollbar.current ? 16 : 50;
    scrollTimeoutRef.current = setTimeout(() => {
      requestAnimationFrame(handleUpdateCurrentPage);
    }, timeout);
  }, [handleUpdateCurrentPage]);

  const handleScroll = useCallback(() => {
    debouncedUpdatePage();
  }, [debouncedUpdatePage]);

  // Attach scroll + scrollbar-drag listeners
  useEffect(() => {
    const container = containerRef.current;
    const scrollElement = container?.querySelector(
      "[data-radix-scroll-area-viewport]"
    );
    if (!scrollElement) return;

    const onMouseDown = (e: Event) => {
      const mouseEvent = e as MouseEvent;
      const rect = scrollElement.getBoundingClientRect();
      const isVertical = mouseEvent.clientX > rect.right - 20;
      const isHorizontal = mouseEvent.clientY > rect.bottom - 20;
      if (isVertical || isHorizontal) isDraggingScrollbar.current = true;
    };

    const onMouseUp = () => {
      if (isDraggingScrollbar.current) {
        isDraggingScrollbar.current = false;
        handleUpdateCurrentPage();
      }
    };

    const onMouseMove = () => {
      if (isDraggingScrollbar.current) debouncedUpdatePage();
    };

    scrollElement.addEventListener("scroll", handleScroll, { passive: true });
    scrollElement.addEventListener("mousedown", onMouseDown);
    document.addEventListener("mouseup", onMouseUp);
    document.addEventListener("mousemove", onMouseMove);

    return () => {
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      scrollElement.removeEventListener("scroll", handleScroll);
      scrollElement.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("mouseup", onMouseUp);
      document.removeEventListener("mousemove", onMouseMove);
    };
  }, [handleScroll, debouncedUpdatePage, handleUpdateCurrentPage]);

  // Re-update page after zoom settles
  useEffect(() => {
    if (renderedPages === numPages && numPages > 0) {
      const timer = setTimeout(() => handleUpdateCurrentPage(), 150);
      return () => clearTimeout(timer);
    }
  }, [zoomLevel, renderedPages, numPages, handleUpdateCurrentPage]);

  const handlePreviousPage = () => {
    if (currentPage <= 1) return;
    const prev = currentPage - 1;
    setCurrentPage(prev);
    setPageInputValue(prev.toString());
    requestAnimationFrame(() => {
      pageRefs.current[prev - 1]?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  const handleNextPage = () => {
    if (currentPage >= numPages) return;
    const next = currentPage + 1;
    setCurrentPage(next);
    setPageInputValue(next.toString());
    requestAnimationFrame(() => {
      pageRefs.current[next - 1]?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  const handlePageInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setPageInputValue(value);
      const page = Math.min(Math.max(parseInt(value) || 1, 1), numPages);
      if (value && !isNaN(page)) {
        setCurrentPage(page);
        pageRefs.current[page - 1]?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    },
    [numPages]
  );

  return {
    numPages,
    setNumPages,
    currentPage,
    setCurrentPage,
    pageInputValue,
    setPageInputValue,
    renderedPages,
    setRenderedPages,
    pageRefs,
    handlePreviousPage,
    handleNextPage,
    handlePageInputChange,
    handleUpdateCurrentPage,
  };
}
